import React, { useRef, useCallback } from "react";

// Interactive descendants that must keep their native behaviour — drag-to-scroll must
// never start on (or swallow clicks of) these.
const INTERACTIVE =
  'button, a, input, select, textarea, label, summary, [role="button"], [contenteditable="true"], [data-no-drag]';

/**
 * Reusable horizontal-scroll wrapper for wide admin tables.
 * - overflow-x-auto + overscroll-contain, visible/accessible scrollbar, focusable region
 * - native trackpad and Shift+wheel work out of the box (overflow-x)
 * - mouse drag-to-scroll via Pointer Events (grab / grabbing), without hijacking clicks
 *   on buttons, links, inputs or selects
 * - vertical wheel is translated to horizontal ONLY while there is room to scroll that
 *   way; at the horizontal edges control is released so the PAGE keeps scrolling
 *   vertically (never blocks page scroll)
 */
export const ScrollableTable = ({
  children,
  className = "",
  ariaLabel = "Tableau — défilement horizontal",
  hint = true,
  testid,
}) => {
  const ref = useRef(null);
  const st = useRef({ down: false, startX: 0, startLeft: 0, moved: false });

  const onPointerDown = useCallback((e) => {
    const el = ref.current;
    if (!el || e.button !== 0) return;
    if (e.target.closest(INTERACTIVE)) return; // never hijack interactive controls
    if (el.scrollWidth <= el.clientWidth) return; // nothing to scroll
    st.current = { down: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false };
    el.classList.add("cursor-grabbing");
    try { el.setPointerCapture(e.pointerId); } catch { /* noop */ }
  }, []);

  const onPointerMove = useCallback((e) => {
    const el = ref.current; const s = st.current;
    if (!el || !s.down) return;
    const dx = e.clientX - s.startX;
    if (Math.abs(dx) > 3) s.moved = true;
    el.scrollLeft = s.startLeft - dx;
  }, []);

  const endDrag = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    st.current.down = false;
    el.classList.remove("cursor-grabbing");
    try { el.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  }, []);

  // Cancel the click that terminates a real drag so underlying controls are not fired.
  const onClickCapture = useCallback((e) => {
    if (st.current.moved) {
      e.stopPropagation();
      e.preventDefault();
      st.current.moved = false;
    }
  }, []);

  const onWheel = useCallback((e) => {
    const el = ref.current;
    if (!el || el.scrollWidth <= el.clientWidth) return; // no horizontal overflow
    if (e.deltaX !== 0) return; // native horizontal (trackpad / Shift+wheel) — leave it
    const atStart = el.scrollLeft <= 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    if ((e.deltaY < 0 && !atStart) || (e.deltaY > 0 && !atEnd)) {
      el.scrollLeft += e.deltaY;
      e.preventDefault(); // consumed for horizontal; released at edges for page scroll
    }
  }, []);

  return (
    <div className="relative">
      <div
        ref={ref}
        role="region"
        aria-label={ariaLabel}
        tabIndex={0}
        data-testid={testid}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
        onWheel={onWheel}
        className={`w-full cursor-grab overflow-x-auto overscroll-contain ${className}`}
      >
        {children}
      </div>
      {hint && (
        <p
          className="mt-1 flex items-center gap-1 text-[11px] text-slate-400 lg:hidden"
          aria-hidden="true"
          data-testid="scroll-hint"
        >
          <span aria-hidden="true">↔</span> Faites glisser horizontalement
        </p>
      )}
    </div>
  );
};

export default ScrollableTable;
